import React, { useState } from "react"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import Box from "@mui/material/Box"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select from "@mui/material/Select"
import { addPermission, updatePermission } from "services/permission"
import { useForm } from "react-hook-form"
import { useSnackbar } from "notistack"
import { StyledPermissionDialog } from "./Permission.style"

const PermissionDlg = (props) => {
  const initPermission = {
    tag: "",
    module: "",
    endpointUrl: "",
    description: "",
  }

  const { enqueueSnackbar } = useSnackbar()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm()

  const { onSubmit, modules } = props
  const [permission, setPermission] = useState(initPermission)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)

  const handleClose = () => {
    setOpen(false)
  }

  const openDlg = (editPermission) => {
    reset()
    setPermission(editPermission || initPermission)
    setValue("tag", editPermission?.tag || "")
    setValue("module", editPermission?.module || "")
    setValue("endpointUrl", editPermission?.endpointUrl || "")
    setValue("description", editPermission?.description || "")
    setIsEditMode(editPermission ? true : false)
    setOpen(true)
  }

  const handleAction = async (data) => {
    setLoading(true)
    try {
      const response = isEditMode
        ? await updatePermission({ ...data, id: permission.id })
        : await addPermission(data)
      if (response.rtnCode === 200) {
        enqueueSnackbar(
          isEditMode ? "Permission updated." : "Permission created.",
          {
            variant: "success",
          }
        )
        onSubmit()
        handleClose()
      } else {
        enqueueSnackbar(
          isEditMode
            ? "Permission update failed."
            : "Permission creation failed.",
          {
            variant: "warning",
          }
        )
      }
    } catch {
      enqueueSnackbar(
        isEditMode
          ? "Permission update failed."
          : "Permission creation failed.",
        {
          variant: "warning",
        }
      )
    } finally {
      setLoading(false)
    }
  }

  const dlg = (
    <StyledPermissionDialog open={open} onClose={handleClose}>
      <DialogTitle>
        <div className="dialog-title-group">
          <h2 className="dialog-title">
            {isEditMode ? "Edit Permission" : "Add New Permission"}
          </h2>
          <p className="dialog-subtitle">
            Configure permission metadata for role access control.
          </p>
        </div>
      </DialogTitle>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogContent>
          <Box>
            <FormControl fullWidth>
              <TextField
                label="Tag"
                variant="outlined"
                size="small"
                fullWidth
                defaultValue={permission.tag}
                {...register("tag", { required: true })}
                error={!!errors?.tag}
                helperText={!!errors?.tag ? "* Tag is required" : " "}
              />
            </FormControl>
          </Box>
          <Box>
            <FormControl fullWidth>
              <TextField
                label="End Point Url"
                variant="outlined"
                size="small"
                fullWidth
                defaultValue={permission.endpointUrl}
                {...register("endpointUrl", { required: true })}
                error={!!errors?.endpointUrl}
                helperText={!!errors?.endpointUrl ? "* Url is required" : " "}
              />
            </FormControl>
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <input
                type="hidden"
                defaultValue={permission.module}
                {...register("module", { required: true })}
              />
              <InputLabel id="permission-modal-select-label">Module</InputLabel>
              <Select
                labelId="permission-modal-select-label"
                id="permission-module-select"
                size="small"
                defaultValue={permission.module}
                label="Module"
                onChange={(e) => {
                  setValue("module", e.target.value)
                }}
              >
                {modules.map((module) => (
                  <MenuItem
                    key={`permission-module-item-${module.tag}`}
                    value={module.tag}
                  >
                    {module.description}
                  </MenuItem>
                ))}
              </Select>
              {!!errors?.module && (
                <div className="error">* Module is required</div>
              )}
            </FormControl>
          </Box>
          <Box>
            <FormControl fullWidth>
              <TextField
                label="Description"
                variant="outlined"
                size="small"
                rows={5}
                multiline
                defaultValue={permission.description}
                {...register("description", { required: true })}
                error={!!errors?.description}
                helperText={
                  !!errors?.description ? "* Description is required" : ""
                }
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-cancel-btn"
            variant="outlined"
            size="medium"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            className="dialog-submit-btn"
            type="submit"
            variant="contained"
            size="medium"
            disabled={loading}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </StyledPermissionDialog>
  )

  return [openDlg, dlg, loading]
}

export default PermissionDlg
